import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties } from "react";
import {
  BUILDING_DEFINITIONS,
  HERO_DEFINITIONS,
  MAX_BUILDING_LEVEL,
  QUEST_DEFINITIONS,
  RESEARCH_DEFINITIONS,
  UNIT_DEFINITIONS,
  calculateCombatPower,
  createDefaultCombatPlan,
  getBuildingCost,
  getBuildingLevel,
  getConstructionDurationMs,
  getResearchCost,
  getResearchDurationMs,
  hasResources,
  isAdjacentToOwnedTile,
  isQuestComplete,
  resourceTypes,
  rosterFromFormation,
} from "@acre/game-core";
import type {
  BattleActorDto,
  BattleEventDto,
  BattleReplayDto,
  BuildingType,
  CaptureResponse,
  CityBuildingDto,
  CombatPlanDto,
  ConstructionStateDto,
  FormationSlotDto,
  HeroId,
  PlayerStateDto,
  QuestId,
  ResearchId,
  ResourceType,
  ResourceWallet,
  TileDto,
  TacticType,
  UnitType,
  WorldMapDto,
} from "@acre/protocol";
import { clearToken, gameApi, getToken, saveToken } from "./api";

type GameTab = "city" | "field" | "army" | "research";
type CityPlot = { x: number; y: number };
type MarchRoute = { from: { x: number; y: number }; to: { x: number; y: number } };
type PixelGlyphName = ResourceType | "gift" | "season" | "book" | "crystal" | "scout" | "grid" | "profile" | "quest" | "gear" | "trophy" | "crest" | "bag" | "chat" | "city" | "map" | "army" | "trade" | "parcel" | "mail" | "tree" | "rock" | "bush" | "stump" | "flowers" | "ruins" | "horse";

const RESOURCE_META: Record<ResourceType, { icon: string; label: string }> = {
  food: { icon: "🌾", label: "อาหาร" },
  wood: { icon: "🪵", label: "ไม้" },
  stone: { icon: "🪨", label: "หิน" },
  coins: { icon: "◉", label: "เหรียญ" },
};

const UNIT_META: Record<UnitType, { icon: string; label: string }> = {
  militia: { icon: "🗡️", label: "ทหารหอกยาว" },
  archer: { icon: "🏹", label: "พรานธนู" },
  cavalry: { icon: "🐎", label: "ทหารม้าดาบ" },
  shield: { icon: "🛡️", label: "ทหารโล่ใหญ่" },
  crossbow: { icon: "➹", label: "พลหน้าไม้" },
};
const UNIT_TYPES = Object.keys(UNIT_META) as UnitType[];

function isUnitType(value: BattleActorDto["type"]): value is UnitType {
  return UNIT_TYPES.includes(value as UnitType);
}

const TACTIC_META: Record<TacticType, { label: string; description: string }> = {
  balanced: { label: "สมดุล", description: "ทุกกองได้จังหวะรบคงที่" },
  ranged_volley: { label: "ระดมยิง", description: "เพิ่มพลังตามสัดส่วนกองยิงไกล" },
  shield_wall: { label: "กำแพงโล่", description: "เพิ่มพลังตามสัดส่วนกองหน้า" },
  cavalry_charge: { label: "ม้าทะลวง", description: "เพิ่มพลังตามสัดส่วนทหารม้า" },
};

const NEUTRAL_META = {
  bandits: { label: "กองโจรชายแดน", mark: "โจร" },
  hedgehog: { label: "ฝูงเม่นสะท้อนคม", mark: "เม่น" },
  goat: { label: "ราชาแพะผา", mark: "แพะ" },
  tiger: { label: "พยัคฆ์เหงียน", mark: "เสือ" },
  rat: { label: "ราชาหนูท่อ", mark: "หนู" },
} as const;

const BUILDING_META: Record<BuildingType, { icon: string; label: string; description: string }> = {
  capital: { icon: "🏯", label: "เมืองหลวง", description: "กำหนดระดับสูงสุดของอาคารทั้งหมด" },
  farm: { icon: "🌾", label: "ไร่นา", description: "ผลิตอาหารสำหรับกองทัพ" },
  lumberyard: { icon: "🪵", label: "โรงเลื่อย", description: "ผลิตไม้สำหรับก่อสร้าง" },
  quarry: { icon: "🪨", label: "เหมืองหิน", description: "ผลิตหินสำหรับอาคารสำคัญ" },
  barn: { icon: "🌽", label: "ยุ้งฉาง", description: "เพิ่มความจุอาหาร 2,500 ต่อระดับ" },
  warehouse: { icon: "📦", label: "คลังสินค้า", description: "เพิ่มความจุไม้และหิน" },
  barracks: { icon: "⚔️", label: "ค่ายทหาร", description: "ปลดล็อกและฝึกกองกำลัง" },
  academy: { icon: "📜", label: "สำนักบัณฑิต", description: "ศูนย์วิจัยสำหรับเมืองระดับ 2" },
};

const BUILDING_GLYPH: Record<BuildingType, PixelGlyphName> = {
  capital: "city",
  farm: "food",
  lumberyard: "wood",
  quarry: "stone",
  barn: "parcel",
  warehouse: "trade",
  barracks: "army",
  academy: "book",
};

const BUILDABLE_TYPES: Exclude<BuildingType, "capital">[] = [
  "farm", "lumberyard", "quarry", "barn", "warehouse", "barracks", "academy",
];

function commandId(): string {
  return crypto.randomUUID();
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat("th-TH").format(value);
}

function walletTotal(wallet: ResourceWallet): number {
  return resourceTypes().reduce((total, resource) => total + wallet[resource], 0);
}

function getRemainingMs(construction: ConstructionStateDto, nowMs: number): number {
  return Math.max(0, Date.parse(construction.completesAt) - nowMs);
}

function getConstructionProgress(construction: ConstructionStateDto, nowMs: number): number {
  const startedAt = Date.parse(construction.startedAt);
  const completesAt = Date.parse(construction.completesAt);
  const duration = Math.max(1, completesAt - startedAt);
  return Math.min(100, Math.max(0, ((nowMs - startedAt) / duration) * 100));
}

function formatCountdown(milliseconds: number): string {
  const totalSeconds = Math.max(0, Math.ceil(milliseconds / 1_000));
  const hours = Math.floor(totalSeconds / 3_600);
  const minutes = Math.floor((totalSeconds % 3_600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) return `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

function formatBuildTime(milliseconds: number): string {
  const seconds = Math.ceil(milliseconds / 1_000);
  if (seconds < 60) return `${seconds} วิ`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return remainingSeconds > 0 ? `${minutes} นาที ${remainingSeconds} วิ` : `${minutes} นาที`;
}

export function App() {
  const [player, setPlayer] = useState<PlayerStateDto | null>(null);
  const [world, setWorld] = useState<WorldMapDto | null>(null);
  const [selectedTile, setSelectedTile] = useState<TileDto | null>(null);
  const [selectedCityPlot, setSelectedCityPlot] = useState<CityPlot>({ x: 2, y: 2 });
  const [marching, setMarching] = useState<MarchRoute | null>(null);
  const [dispatchTarget, setDispatchTarget] = useState<TileDto | null>(null);
  const [battleResult, setBattleResult] = useState<CaptureResponse | null>(null);
  const [questsOpen, setQuestsOpen] = useState(false);
  const [tab, setTab] = useState<GameTab>("field");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [message, setMessage] = useState("กำลังเชื่อมต่ออาณาจักร...");

  const applyBootstrap = useCallback((nextPlayer: PlayerStateDto, nextWorld: WorldMapDto) => {
    setPlayer(nextPlayer);
    setWorld(nextWorld);
    setSelectedTile((current) => {
      if (current) return nextWorld.tiles.find((tile) => tile.x === current.x && tile.y === current.y) ?? null;
      return nextWorld.tiles.find((tile) => tile.city?.ownerId === nextPlayer.id) ?? null;
    });
  }, []);

  const refresh = useCallback(async () => {
    const data = await gameApi.bootstrap();
    applyBootstrap(data.player, data.world);
  }, [applyBootstrap]);

  useEffect(() => {
    if (!getToken()) {
      setMessage("พร้อมเริ่มอาณาจักรใหม่");
      setLoading(false);
      return;
    }
    refresh()
      .then(() => setMessage("ผลผลิตและชายแดนอัปเดตแล้ว"))
      .catch(() => {
        clearToken();
        setMessage("เซสชันหมดอายุ กรุณาเริ่มใหม่");
      })
      .finally(() => setLoading(false));
  }, [refresh]);

  const activeConstruction = player?.city?.buildings.find((building) => building.construction);
  const activeResearch = player?.activeResearch;

  useEffect(() => {
    const timer = window.setInterval(() => setNowMs(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!activeConstruction?.construction) return;
    const delay = Math.max(250, getRemainingMs(activeConstruction.construction, Date.now()) + 250);
    const timer = window.setTimeout(() => {
      refresh()
        .then(() => setMessage(`${BUILDING_META[activeConstruction.type].label}ก่อสร้างเสร็จแล้ว`))
        .catch(() => setMessage("ตรวจสอบงานก่อสร้างไม่สำเร็จ กรุณาลองใหม่"));
    }, delay);
    return () => window.clearTimeout(timer);
  }, [activeConstruction?.id, activeConstruction?.construction?.completesAt, activeConstruction?.type, refresh]);

  useEffect(() => {
    if (!activeResearch) return;
    const delay = Math.max(250, Date.parse(activeResearch.completesAt) - Date.now() + 250);
    const timer = window.setTimeout(() => {
      refresh()
        .then(() => setMessage(`${RESEARCH_DEFINITIONS[activeResearch.researchId].name} วิจัยสำเร็จแล้ว`))
        .catch(() => setMessage("ตรวจสอบงานวิจัยไม่สำเร็จ กรุณาลองใหม่"));
    }, delay);
    return () => window.clearTimeout(timer);
  }, [activeResearch?.completesAt, activeResearch?.researchId, refresh]);

  const startGame = async () => {
    setBusy(true);
    setMessage("กำลังจัดสรรที่ดินและตั้งเมืองหลวง...");
    try {
      const auth = await gameApi.createGuest();
      saveToken(auth.token);
      const data = await gameApi.bootstrap();
      applyBootstrap(data.player, data.world);
      setMessage("เมืองหลวงพร้อมแล้ว เลือกพื้นที่ว่างในเมืองเพื่อสร้างค่ายทหาร");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "เริ่มเกมไม่สำเร็จ");
    } finally {
      setBusy(false);
    }
  };

  const build = async (buildingType: Exclude<BuildingType, "capital">, plot: CityPlot) => {
    setBusy(true);
    setMessage(`กำลังก่อสร้าง${BUILDING_META[buildingType].label}...`);
    try {
      const result = await gameApi.build({ commandId: commandId(), buildingType, ...plot });
      setPlayer(result.player);
      setSelectedCityPlot(plot);
      const duration = result.building.construction
        ? formatBuildTime(getRemainingMs(result.building.construction, Date.now()))
        : "ไม่นาน";
      setMessage(`เริ่มสร้าง${BUILDING_META[buildingType].label}แล้ว · รอ ${duration}`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "ก่อสร้างไม่สำเร็จ");
    } finally {
      setBusy(false);
    }
  };

  const upgradeBuilding = async (building: CityBuildingDto) => {
    setBusy(true);
    setMessage(`กำลังอัปเกรด${BUILDING_META[building.type].label}...`);
    try {
      const result = await gameApi.upgradeBuilding({ commandId: commandId(), buildingId: building.id });
      setPlayer(result.player);
      const construction = result.building.construction;
      setMessage(
        construction
          ? `กำลังอัปเกรด${BUILDING_META[building.type].label}เป็นระดับ ${construction.targetLevel} · รอ ${formatBuildTime(getRemainingMs(construction, Date.now()))}`
          : `${BUILDING_META[building.type].label}อัปเกรดแล้ว`,
      );
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "อัปเกรดไม่สำเร็จ");
    } finally {
      setBusy(false);
    }
  };

  const collectCity = async () => {
    setBusy(true);
    try {
      const result = await gameApi.collectCity(commandId());
      setPlayer(result.player);
      const total = walletTotal(result.collected);
      setMessage(total > 0 ? `เก็บผลผลิต ${formatNumber(total)} หน่วยแล้ว` : "ยังไม่มีผลผลิตให้เก็บ");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "เก็บผลผลิตไม่สำเร็จ");
    } finally {
      setBusy(false);
    }
  };

  const recruit = async (unitType: UnitType) => {
    setBusy(true);
    try {
      const result = await gameApi.recruit({ commandId: commandId(), unitType, quantity: 1 });
      setPlayer(result.player);
      setMessage(`ฝึก${UNIT_META[unitType].label}สำเร็จ ใช้อาหาร ${result.spentFood}`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "ฝึกทหารไม่สำเร็จ");
    } finally {
      setBusy(false);
    }
  };

  const startResearch = async (researchId: ResearchId) => {
    setBusy(true);
    setMessage(`กำลังเริ่มวิจัย${RESEARCH_DEFINITIONS[researchId].name}...`);
    try {
      const result = await gameApi.startResearch(commandId(), researchId);
      setPlayer(result.player);
      setMessage(`เริ่มวิจัย${RESEARCH_DEFINITIONS[researchId].name} · รอ ${formatBuildTime(Date.parse(result.research.completesAt) - Date.now())}`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "เริ่มวิจัยไม่สำเร็จ");
    } finally {
      setBusy(false);
    }
  };

  const claimQuest = async (questId: QuestId) => {
    setBusy(true);
    try {
      const result = await gameApi.claimQuest(commandId(), questId);
      setPlayer(result.player);
      setMessage(`รับรางวัลภารกิจ ${QUEST_DEFINITIONS[questId].name} แล้ว`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "รับรางวัลไม่สำเร็จ");
    } finally {
      setBusy(false);
    }
  };

  const prepareCapture = () => {
    if (!selectedTile || !world) return;
    const source = world.tiles.find(
      (tile) => tile.ownerId === player?.id && Math.abs(tile.x - selectedTile.x) + Math.abs(tile.y - selectedTile.y) === 1,
    );
    if (!source) {
      setMessage("ต้องยึดพื้นที่ที่ติดกับชายแดนของคุณ");
      return;
    }
    setDispatchTarget(selectedTile);
    setMessage(`เลือกกองทัพสำหรับพื้นที่ ${selectedTile.x}, ${selectedTile.y}`);
  };

  const capture = async (plan: CombatPlanDto) => {
    if (!dispatchTarget || !world) return;
    const target = dispatchTarget;
    const source = world.tiles.find(
      (tile) => tile.ownerId === player?.id && Math.abs(tile.x - target.x) + Math.abs(tile.y - target.y) === 1,
    );
    if (!source) {
      setDispatchTarget(null);
      setMessage("ต้องยึดพื้นที่ที่ติดกับชายแดนของคุณ");
      return;
    }
    setDispatchTarget(null);
    setBusy(true);
    setMarching({ from: { x: source.x, y: source.y }, to: { x: target.x, y: target.y } });
    setMessage("กองทัพกำลังขยายแนวชายแดน...");
    try {
      const [result] = await Promise.all([
        gameApi.capture({ commandId: commandId(), x: target.x, y: target.y, units: rosterFromFormation(plan.formation), plan }),
        new Promise((resolve) => setTimeout(resolve, 900)),
      ]);
      setMarching(null);
      if (result.battle) {
        setBattleResult(result);
        setMessage("กองทัพปะทะศัตรูแล้ว");
        return;
      }
      await refresh();
      const reasonMessage = {
        already_owned: "พื้นที่นี้เป็นของคุณแล้ว",
        occupied: "พื้นที่นี้มีเจ้าเมืองอื่นครอบครอง",
        insufficient_power: "กองทัพยังแข็งแกร่งไม่พอ",
        not_adjacent: "ต้องยึดพื้นที่ที่ติดกับชายแดนของคุณ",
        unreachable: "กองทัพไม่สามารถข้ามผืนน้ำได้",
      }[result.reason ?? "insufficient_power"];
      setMessage(result.success ? `ยึดพื้นที่สำเร็จ ผลิต +${result.tile.productionPerHour}/ชม.` : reasonMessage);
      setBusy(false);
    } catch (error) {
      setMarching(null);
      setMessage(error instanceof Error ? error.message : "ยึดพื้นที่ไม่สำเร็จ");
      setBusy(false);
    }
  };

  const finishBattle = async () => {
    if (!battleResult) return;
    await refresh();
    setSelectedTile(battleResult.tile);
    setMessage(
      battleResult.success
        ? `ยึดพื้นที่สำเร็จ ผลิต +${battleResult.tile.productionPerHour}/ชม.`
        : "กองทัพพ่ายแพ้ กำลังเดินทัพถูกคืนแล้ว",
    );
    setBattleResult(null);
    setBusy(false);
  };

  if (loading) {
    return <main className="boot-screen"><div className="brand-mark">N</div><p>{message}</p></main>;
  }
  if (!player || !world) return <Landing busy={busy} message={message} onStart={startGame} />;

  return (
    <main className={`game-shell tab-${tab}`}>
      <header className="topbar">
        <div>
          <span className="eyebrow">{player.city?.name ?? "อาณาจักรของคุณ"}</span>
          <h1>{player.displayName}</h1>
        </div>
        <button className="top-quest-button" aria-label="เปิดสมุดภารกิจ" onClick={() => setQuestsOpen(true)}><PixelGlyph name="quest" /><i>{(Object.keys(QUEST_DEFINITIONS) as QuestId[]).filter((questId) => isQuestComplete(player, questId) && !player.claimedQuests.includes(questId)).length}</i></button>
        <div className="stamina"><PixelGlyph name="season" />{player.stamina}/20</div>
      </header>
      <ResourceBar player={player} />
      <section className="status-strip" aria-live="polite">
        <span className={busy ? "status-dot is-busy" : "status-dot"} />
        <span>{message}</span>
      </section>

      <div className="screen-content">
        {tab === "city" && player.city && (
          <CityScreen
            player={player}
            selectedPlot={selectedCityPlot}
            busy={busy}
            nowMs={nowMs}
            onSelectPlot={setSelectedCityPlot}
            onBuild={build}
            onUpgrade={upgradeBuilding}
            onCollect={collectCity}
          />
        )}
        {tab === "field" && (
          <WorldScreen
            player={player}
            world={world}
            selectedTile={selectedTile}
            busy={busy}
            onSelect={setSelectedTile}
            onCapture={prepareCapture}
            onOpenCity={() => setTab("city")}
            onOpenQuests={() => setQuestsOpen(true)}
            marching={marching}
            message={message}
          />
        )}
        {tab === "army" && <ArmyScreen player={player} busy={busy} onRecruit={recruit} />}
        {tab === "research" && <ResearchScreen player={player} busy={busy} nowMs={nowMs} onResearch={startResearch} />}
      </div>

      <nav className="bottom-nav" aria-label="เมนูหลัก">
        <NavButton active={tab === "city"} icon="city" label="เมือง" onClick={() => setTab("city")} />
        <NavButton active={tab === "field"} icon="map" label="แผนที่" onClick={() => setTab("field")} />
        <NavButton active={tab === "army"} icon="army" label="กองทัพ" onClick={() => setTab("army")} />
        <NavButton active={tab === "research"} icon="book" label="วิจัย" onClick={() => setTab("research")} />
      </nav>
      {dispatchTarget && (
        <ArmyDispatchModal
          player={player}
          target={dispatchTarget}
          busy={busy}
          onCancel={() => {
            setDispatchTarget(null);
            setMessage("ยกเลิกการจัดกองทัพแล้ว");
          }}
          onConfirm={capture}
        />
      )}
      {battleResult?.battle && <BattleScene result={battleResult} replay={battleResult.battle} onComplete={finishBattle} />}
      {questsOpen && <QuestPanel player={player} busy={busy} onClose={() => setQuestsOpen(false)} onClaim={claimQuest} />}
    </main>
  );
}

function Landing({ busy, message, onStart }: { busy: boolean; message: string; onStart: () => void }) {
  return (
    <main className="landing">
      <div className="landing-sun" />
      <div className="landing-copy">
        <div className="brand-mark">N</div>
        <span className="eyebrow">NINETY</span>
        <h1>หนึ่งผืนดิน<br />สู่หนึ่งอาณาจักร</h1>
        <p>สร้างเมืองบนผืนดิน ฝึกกองทัพ และขยายเขตแดนบนโลกออนไลน์ที่ไม่เคยหยุดนิ่ง</p>
        <button className="primary-button" disabled={busy} onClick={onStart}>
          {busy ? "กำลังตั้งเมือง..." : "เริ่มสร้างอาณาจักร"}
        </button>
        <small>{message}</small>
      </div>
    </main>
  );
}

function ResourceBar({ player }: { player: PlayerStateDto }) {
  return (
    <section className="resource-bar">
      {resourceTypes().map((resource) => (
        <div className="resource-pill" key={resource} title={RESOURCE_META[resource].label}>
          <PixelGlyph name={resource} />
          <strong>{formatNumber(player.resources[resource])}</strong>
          {player.city && <small>/{formatNumber(player.city.storageCaps[resource])}</small>}
        </div>
      ))}
    </section>
  );
}

function CityScreen({
  player,
  selectedPlot,
  busy,
  nowMs,
  onSelectPlot,
  onBuild,
  onUpgrade,
  onCollect,
}: {
  player: PlayerStateDto;
  selectedPlot: CityPlot;
  busy: boolean;
  nowMs: number;
  onSelectPlot: (plot: CityPlot) => void;
  onBuild: (type: Exclude<BuildingType, "capital">, plot: CityPlot) => void;
  onUpgrade: (building: CityBuildingDto) => void;
  onCollect: () => void;
}) {
  const city = player.city;
  if (!city) return null;
  const plots = Array.from({ length: city.width * city.height }, (_, index) => ({
    x: index % city.width,
    y: Math.floor(index / city.width),
  }));
  const selectedBuilding = city.buildings.find(
    (building) => building.x === selectedPlot.x && building.y === selectedPlot.y,
  );
  const claimableTotal = walletTotal(city.claimable);
  const activeConstruction = city.buildings.find((building) => building.construction);

  return (
    <section className="city-screen">
      <div className="city-overview">
        <div>
          <span className="eyebrow">CAPITAL · LEVEL {city.level}</span>
          <h2>{city.name}</h2>
          <p>พิกัด {city.location.x}, {city.location.y} · {player.territoryCount} ผืนดิน</p>
        </div>
        <button className="collect-button" disabled={busy || claimableTotal < 1} onClick={onCollect}>
          <span>เก็บผลผลิต</span>
          <strong>+{formatNumber(claimableTotal)}</strong>
        </button>
      </div>

      <div className="income-row" aria-label="ผลผลิตต่อชั่วโมง">
        {resourceTypes().map((resource) => (
          <span key={resource}><PixelGlyph name={resource} />+{formatNumber(city.productionPerHour[resource])}/ชม.</span>
        ))}
      </div>

      {activeConstruction?.construction && (
        <div className="construction-banner" aria-live="polite">
          <span className="construction-banner-icon"><PixelGlyph name="gear" /></span>
          <div>
            <span className="eyebrow">ช่างก่อสร้างกำลังทำงาน</span>
            <strong>
              {activeConstruction.construction.kind === "build" ? "สร้าง" : "อัปเกรด"}
              {BUILDING_META[activeConstruction.type].label} · Lv.{activeConstruction.construction.targetLevel}
            </strong>
            <div className="construction-progress"><i style={{ width: `${getConstructionProgress(activeConstruction.construction, nowMs)}%` }} /></div>
          </div>
          <time dateTime={activeConstruction.construction.completesAt}>
            {formatCountdown(getRemainingMs(activeConstruction.construction, nowMs))}
          </time>
        </div>
      )}

      <div className="section-heading">
        <div><span className="eyebrow">CITY FIELD · 5 × 5</span><h2>ผังเมือง</h2></div>
        <span className="territory-count">{city.buildings.length}/25 อาคาร</span>
      </div>

      <div className="city-field" role="grid" aria-label="ผังเมือง">
        {plots.map((plot) => {
          const building = city.buildings.find((item) => item.x === plot.x && item.y === plot.y);
          const selected = selectedPlot.x === plot.x && selectedPlot.y === plot.y;
          const constructing = building?.construction;
          return (
            <button
              key={`${plot.x}:${plot.y}`}
              className={`city-plot ${building ? `has-building building-${building.type}` : "is-empty"} ${constructing ? "is-constructing" : ""} ${selected ? "is-selected" : ""}`}
              role="gridcell"
              aria-label={building ? `${BUILDING_META[building.type].label} ${constructing ? `กำลังก่อสร้างระดับ ${constructing.targetLevel}` : `ระดับ ${building.level}`}` : `พื้นที่ว่าง ${plot.x}, ${plot.y}`}
              onClick={() => onSelectPlot(plot)}
            >
              <span className="plot-ground" />
              {building ? (
                <>
                  <span className="building-icon"><PixelGlyph name={building.level === 0 ? "gear" : BUILDING_GLYPH[building.type]} /></span>
                  <b>{constructing ? `→Lv.${constructing.targetLevel}` : `Lv.${building.level}`}</b>
                  {constructing && (
                    <span className="plot-construction-progress">
                      <i style={{ width: `${getConstructionProgress(constructing, nowMs)}%` }} />
                    </span>
                  )}
                </>
              ) : <span className="empty-mark">+</span>}
            </button>
          );
        })}
      </div>

      <CityPlotInspector
        player={player}
        plot={selectedPlot}
        building={selectedBuilding}
        busy={busy}
        nowMs={nowMs}
        activeConstruction={activeConstruction}
        onBuild={onBuild}
        onUpgrade={onUpgrade}
      />
    </section>
  );
}

function CityPlotInspector({
  player,
  plot,
  building,
  busy,
  nowMs,
  activeConstruction,
  onBuild,
  onUpgrade,
}: {
  player: PlayerStateDto;
  plot: CityPlot;
  building?: CityBuildingDto;
  busy: boolean;
  nowMs: number;
  activeConstruction?: CityBuildingDto;
  onBuild: (type: Exclude<BuildingType, "capital">, plot: CityPlot) => void;
  onUpgrade: (building: CityBuildingDto) => void;
}) {
  const city = player.city;
  if (!city) return null;
  if (building) {
    if (building.construction) {
      const progress = getConstructionProgress(building.construction, nowMs);
      return (
        <article className="city-inspector construction-inspector">
          <div className="inspector-title">
            <span className="inspector-icon"><PixelGlyph name="gear" /></span>
            <div>
              <span className="eyebrow">PLOT {plot.x}, {plot.y} · CONSTRUCTION</span>
              <h3>{building.construction.kind === "build" ? "กำลังสร้าง" : "กำลังอัปเกรด"}{BUILDING_META[building.type].label}</h3>
            </div>
          </div>
          <div className="construction-countdown">
            <div><span>เป้าหมาย</span><strong>ระดับ {building.construction.targetLevel}</strong></div>
            <time dateTime={building.construction.completesAt}>{formatCountdown(getRemainingMs(building.construction, nowMs))}</time>
          </div>
          <div className="construction-progress is-large"><i style={{ width: `${progress}%` }} /></div>
          <p>ช่างก่อสร้างทำงานต่อบนเซิร์ฟเวอร์ แม้ปิดหน้าเกมเวลายังคงเดิน</p>
          <button className="action-button" disabled>กำลังก่อสร้าง · {Math.floor(progress)}%</button>
        </article>
      );
    }
    const targetLevel = building.level + 1;
    const cost = getBuildingCost(building.type, targetLevel);
    const constructionTime = getConstructionDurationMs(building.type, targetLevel);
    const capitalBlocked = building.type !== "capital" && targetLevel > city.level;
    const canUpgrade = !activeConstruction && building.level < MAX_BUILDING_LEVEL && !capitalBlocked && hasResources(player.resources, cost);
    return (
      <article className="city-inspector">
        <div className="inspector-title">
          <span className="inspector-icon"><PixelGlyph name={BUILDING_GLYPH[building.type]} /></span>
          <div><span className="eyebrow">PLOT {plot.x}, {plot.y}</span><h3>{BUILDING_META[building.type].label} · Lv.{building.level}</h3></div>
        </div>
        <p>{BUILDING_META[building.type].description}</p>
        <CostLine wallet={cost} />
        <div className="build-time-line"><span>⏱ ระยะเวลา</span><strong>{formatBuildTime(constructionTime)}</strong></div>
        <button className="action-button" disabled={busy || !canUpgrade} onClick={() => onUpgrade(building)}>
          {building.level >= MAX_BUILDING_LEVEL ? "ระดับสูงสุด" : activeConstruction ? "ช่างก่อสร้างไม่ว่าง" : capitalBlocked ? "ต้องอัปเกรดเมืองหลวง" : `อัปเกรดเป็น Lv.${targetLevel}`}
        </button>
      </article>
    );
  }

  return (
    <article className="city-inspector">
      <div><span className="eyebrow">EMPTY PLOT · {plot.x}, {plot.y}</span><h3>เลือกอาคารที่จะก่อสร้าง</h3></div>
      <div className="building-catalog">
        {BUILDABLE_TYPES.map((type) => {
          const definition = BUILDING_DEFINITIONS[type];
          const cost = getBuildingCost(type, 1);
          const count = city.buildings.filter((item) => item.type === type).length;
          const locked = city.level < definition.minCapitalLevel || count >= definition.maxCount;
          const constructionTime = getConstructionDurationMs(type, 1);
          return (
            <button
              key={type}
              className="building-choice"
              disabled={busy || Boolean(activeConstruction) || locked || !hasResources(player.resources, cost)}
              onClick={() => onBuild(type, plot)}
            >
              <span><PixelGlyph name={BUILDING_GLYPH[type]} /></span>
              <strong>{BUILDING_META[type].label}</strong>
              <small>{locked ? "ยังไม่ปลดล็อก" : activeConstruction ? "ช่างก่อสร้างไม่ว่าง" : `${formatCost(cost)} · ⏱ ${formatBuildTime(constructionTime)}`}</small>
            </button>
          );
        })}
      </div>
    </article>
  );
}

function CostLine({ wallet }: { wallet: ResourceWallet }) {
  const entries = resourceTypes().filter((resource) => wallet[resource] > 0);
  return <div className="cost-line"><span>ค่าใช้จ่าย</span>{entries.map((resource) => <b key={resource}><PixelGlyph name={resource} />{wallet[resource]}</b>)}</div>;
}

function formatCost(wallet: ResourceWallet): string {
  return resourceTypes().filter((resource) => wallet[resource] > 0).map((resource) => `${RESOURCE_META[resource].label} ${wallet[resource]}`).join(" · ");
}

function WorldScreen({
  player,
  world,
  selectedTile,
  busy,
  onSelect,
  onCapture,
  onOpenCity,
  onOpenQuests,
  marching,
  message,
}: {
  player: PlayerStateDto;
  world: WorldMapDto;
  selectedTile: TileDto | null;
  busy: boolean;
  onSelect: (tile: TileDto) => void;
  onCapture: () => void;
  onOpenCity: () => void;
  onOpenQuests: () => void;
  marching: MarchRoute | null;
  message: string;
}) {
  const mapViewportRef = useRef<HTMLDivElement>(null);
  const selectedIsMine = selectedTile?.ownerId === player.id;
  const selectedIsMyCity = selectedTile?.city?.ownerId === player.id;
  const adjacent = selectedTile ? isAdjacentToOwnedTile(world.tiles, player.id, selectedTile.x, selectedTile.y) : false;
  const ownedFields = useMemo(
    () => new Set(world.tiles.filter((tile) => tile.ownerId === player.id).map((tile) => `${tile.x}:${tile.y}`)),
    [player.id, world.tiles],
  );
  const mapStats = useMemo(() => ({
    mine: world.tiles.filter((tile) => tile.ownerId === player.id).length,
    open: world.tiles.filter((tile) => !tile.ownerId && tile.terrain !== "water").length,
  }), [player.id, world.tiles]);
  const cityTile = world.tiles.find((tile) => tile.city?.ownerId === player.id);

  const focusTile = (tile: TileDto | undefined) => {
    if (tile) onSelect(tile);
  };

  useEffect(() => {
    const viewport = mapViewportRef.current;
    if (!viewport || !selectedTile) return;
    const target = viewport.querySelector<HTMLElement>(`[data-field="${selectedTile.x}:${selectedTile.y}"]`);
    if (!target) return;
    viewport.scrollTo({
      left: Math.max(0, target.offsetLeft - viewport.clientWidth / 2 + target.clientWidth / 2),
      top: Math.max(0, target.offsetTop - viewport.clientHeight / 2 + target.clientHeight / 2),
      behavior: "smooth",
    });
  }, [selectedTile]);

  return (
    <section className="world-screen">
      <div className="map-viewport" ref={mapViewportRef}>
        <div className="map-field" role="grid" aria-label="แผนที่โลก" style={{ gridTemplateColumns: `repeat(${world.width}, var(--field-size))` }}>
          {world.tiles.map((tile) => {
            const isMine = tile.ownerId === player.id;
            const isSelected = selectedTile?.x === tile.x && selectedTile.y === tile.y;
            const edges = isMine
              ? [
                  !ownedFields.has(`${tile.x}:${tile.y - 1}`) && "edge-top",
                  !ownedFields.has(`${tile.x + 1}:${tile.y}`) && "edge-right",
                  !ownedFields.has(`${tile.x}:${tile.y + 1}`) && "edge-bottom",
                  !ownedFields.has(`${tile.x - 1}:${tile.y}`) && "edge-left",
                ].filter(Boolean).join(" ")
              : "";
            return (
              <button
                className={`field-tile terrain-${tile.terrain} node-${tile.nodeType} ${isMine ? "is-mine" : ""} ${tile.ownerId && !isMine ? "is-occupied" : ""} ${isSelected ? "is-selected" : ""} ${edges}`}
                key={`${tile.x}:${tile.y}`}
                data-field={`${tile.x}:${tile.y}`}
                data-x={tile.x}
                data-y={tile.y}
                role="gridcell"
                aria-label={tile.city ? `${tile.city.name} ระดับ ${tile.city.level}` : `พื้นที่ ${tile.x}, ${tile.y} ระดับ ${tile.level}`}
                onClick={() => onSelect(tile)}
              >
                <PixelMapNode tile={tile} isMine={isMine} />
                {tile.city && <span className="city-label"><i />{tile.city.name}</span>}
                {tile.nodeType !== "capital" && isSelected && <span className="field-level">{tile.level}</span>}
                {isMine && !tile.city && <span className="field-flag"><PixelGlyph name="crest" /></span>}
                {marching?.to.x === tile.x && marching.to.y === tile.y && (
                  <span
                    className="marching-unit"
                    style={{
                      "--march-x": `${(marching.from.x - marching.to.x) * 50}px`,
                      "--march-y": `${(marching.from.y - marching.to.y) * 50}px`,
                    } as CSSProperties}
                  >
                    <PixelSoldier action="walk" side="attacker" />
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      <WorldHud player={player} mine={mapStats.mine} open={mapStats.open} busy={busy} />

      <aside className="world-side-toolbar" aria-label="เครื่องมือแผนที่">
        <button aria-label="กลับไปเมืองของฉัน" onClick={() => focusTile(cityTile)}><PixelGlyph name="grid" /></button>
        <button aria-label="ข้อมูลผู้เล่น" onClick={onOpenCity}><PixelGlyph name="profile" /></button>
        <button aria-label="เปิดสมุดภารกิจ" onClick={onOpenQuests}><PixelGlyph name="quest" /></button>
        <button aria-label="ค้นหาพื้นที่ระดับสูง" onClick={() => focusTile([...world.tiles].filter((tile) => tile.terrain !== "water").sort((a, b) => b.level - a.level)[0])}><PixelGlyph name="gear" /></button>
        <button aria-label="อันดับอาณาจักร" onClick={() => focusTile(world.tiles.find((tile) => tile.ownerId && tile.ownerId !== player.id) ?? cityTile)}><PixelGlyph name="trophy" /></button>
      </aside>

      <div className="world-action-stack">
        <button aria-label="ชายแดนของฉัน" onClick={() => focusTile(world.tiles.find((tile) => tile.ownerId === player.id && !tile.city) ?? cityTile)}><PixelGlyph name="crest" /></button>
        <button aria-label="แหล่งทรัพยากร" onClick={() => focusTile(world.tiles.find((tile) => !tile.ownerId && tile.terrain !== "water"))}><PixelGlyph name="bag" /></button>
      </div>

      <button className="world-chat" aria-label="แชตอาณาจักร"><PixelGlyph name="chat" /></button>

      <div className="world-utility-dock" aria-label="ทางลัด">
        <button aria-label="การค้า" onClick={() => focusTile(cityTile)}><PixelGlyph name="trade" /></button>
        <button aria-label="ภารกิจพื้นที่" onClick={() => focusTile(world.tiles.find((tile) => !tile.ownerId && tile.terrain !== "water" && tile.level === 1))}><PixelGlyph name="parcel" /></button>
        <button aria-label="รายงานสนาม" onClick={() => focusTile(selectedTile ?? cityTile)}><PixelGlyph name="mail" /></button>
      </div>

      <div className="tile-inspector">
        {selectedTile ? (
          <>
            <div className="tile-details">
              <span className="eyebrow">FIELD {selectedTile.x}, {selectedTile.y}</span>
              <h3>{selectedTile.city?.name ?? (selectedTile.neutralUnit ? NEUTRAL_META[selectedTile.neutralUnit].label : `${RESOURCE_META[selectedTile.resourceType].label} ระดับ ${selectedTile.level}`)}</h3>
              <p>{selectedTile.nodeType === "capital" ? `เมือง Lv.${selectedTile.city?.level ?? selectedTile.level} · ป้องกัน ${selectedTile.defense}` : `ป้องกัน ${selectedTile.defense} · +${selectedTile.productionPerHour}/ชม.`}</p>
              {!selectedIsMine && !adjacent && selectedTile.terrain !== "water" && <small>ต้องขยายจากพื้นที่ที่ติดกัน</small>}
            </div>
            {selectedIsMyCity ? (
              <button className="capture-button" disabled={busy} onClick={onOpenCity}>เข้าเมือง</button>
            ) : (
              <button
                className="capture-button"
                disabled={busy || selectedIsMine || !adjacent || selectedTile.terrain === "water" || Boolean(selectedTile.ownerId)}
                onClick={onCapture}
              >
                {selectedIsMine ? "ครอบครองแล้ว" : busy ? "กำลังเดินทัพ" : "ยึดพื้นที่"}
              </button>
            )}
          </>
        ) : <p>แตะพื้นที่บนแผนที่เพื่อดูรายละเอียด</p>}
      </div>

      <div className="world-status" aria-live="polite"><span className={busy ? "status-dot is-busy" : "status-dot"} />{message}</div>
    </section>
  );
}

function WorldHud({ player, mine, open, busy }: { player: PlayerStateDto; mine: number; open: number; busy: boolean }) {
  const production = player.city?.productionPerHour;
  const gain = (resource: ResourceType) => (production?.[resource] ?? 0) + player.territoryYieldPerHour[resource];
  return (
    <div className="world-hud">
      <div className="world-resource-columns">
        <div>
          {(["food", "wood", "stone"] as ResourceType[]).map((resource) => (
            <div className={`pixel-resource resource-${resource}`} key={resource}>
              <PixelGlyph name={resource} />
              <strong>{formatNumber(player.resources[resource])}</strong>
              <small>+{formatNumber(gain(resource))}</small>
              <i><b style={{ width: `${Math.min(100, player.resources[resource] / Math.max(1, player.city?.storageCaps[resource] ?? 1) * 100)}%` }} /></i>
            </div>
          ))}
        </div>
        <div>
          <div className="pixel-resource resource-coins"><PixelGlyph name="coins" /><strong>{formatNumber(player.resources.coins)}</strong><button aria-label="ซื้อเหรียญ">+</button></div>
          <div className="pixel-resource resource-gift"><PixelGlyph name="gift" /><strong>{player.stamina}</strong><small>/50</small><i><b style={{ width: `${Math.min(100, player.stamina * 2)}%` }} /></i></div>
          <div className="pixel-resource resource-season"><PixelGlyph name="season" /><span className="season-swatches"><i /><i /><i /><i /></span></div>
        </div>
      </div>
      <div className="world-mini-stats">
        <span><PixelGlyph name="book" />{mine}</span>
        <span><PixelGlyph name="crystal" />{player.territoryCount}</span>
        <span><PixelGlyph name="scout" />{open}</span>
      </div>
      {busy && <span className="world-busy-mark">กำลังทำงาน…</span>}
    </div>
  );
}

function PixelMapNode({ tile, isMine }: { tile: TileDto; isMine: boolean }) {
  if (tile.terrain === "water") return <span className="water-ripples"><i /><i /></span>;
  if (tile.nodeType === "capital") return <span className="map-node map-city"><PixelGlyph name="city" /></span>;
  if (tile.nodeType === "ruins") return <span className="map-node map-ruins"><PixelGlyph name="ruins" />{tile.neutralUnit && <i>{NEUTRAL_META[tile.neutralUnit].mark}</i>}</span>;
  if (tile.neutralUnit && !isMine) return <span className={`map-node map-wild wild-${tile.neutralUnit}`}><PixelSoldier action="idle" side="defender" /><i>{NEUTRAL_META[tile.neutralUnit].mark}</i></span>;
  const seed = Math.abs(tile.x * 37 + tile.y * 61 + tile.x * tile.y * 7);
  const decoration: PixelGlyphName = isMine && seed % 5 === 0
    ? "horse"
    : tile.resourceType === "wood"
      ? seed % 3 === 0 ? "tree" : seed % 3 === 1 ? "bush" : "stump"
      : tile.resourceType === "stone"
        ? "rock"
        : seed % 4 === 0 ? "flowers" : seed % 4 === 1 ? "bush" : "stump";
  return <span className={`map-node map-${decoration}`}><PixelGlyph name={decoration} /></span>;
}

function ArmyDispatchModal({
  player,
  target,
  busy,
  onCancel,
  onConfirm,
}: {
  player: PlayerStateDto;
  target: TileDto;
  busy: boolean;
  onCancel: () => void;
  onConfirm: (plan: CombatPlanDto) => void;
}) {
  const initialPlan = createDefaultCombatPlan(player.units);
  const [formation, setFormation] = useState<FormationSlotDto[]>(initialPlan.formation);
  const [tactic, setTactic] = useState<TacticType>("balanced");
  const [heroId, setHeroId] = useState<HeroId>(player.unlockedHeroes[0] ?? "sun-wu");
  const unitTypes = Object.keys(UNIT_META) as UnitType[];
  const selected = rosterFromFormation(formation);
  const plan: CombatPlanDto = { formation, tactic, heroId };
  const selectedPower = calculateCombatPower(plan, player.technology, target.neutralUnit);
  const selectedCount = formation.reduce((count, slot) => count + slot.quantity, 0);
  const defenderCount = Math.max(1, Math.ceil(target.defense / 2));
  const favorable = selectedPower >= target.defense;
  const enemyTips: Record<string, string> = {
    bandits: "ศัตรูทั่วไป: ส่งกองยิงไกลก่อน แล้วให้โล่ปิดท้าย",
    hedgehog: "หนามสะท้อนความเสียหาย: กองยิงไกลควรเข้าก่อนและจบไว",
    goat: "ราชาแพะใช้ HP ของเป้าหมาย: หลีกเลี่ยงกองโล่ใหญ่",
    tiger: "พยัคฆ์กระโจนถึงแนวหลัง: ส่งกองหน้าก่อน",
    rat: "ราชาหนูบุกเร็ว: ส่งกองหน้าก่อนคุ้มกันกองยิง",
  };

  const changeQuantity = (index: number, delta: number) => {
    setFormation((current) => {
      const slot = current[index];
      if (!slot) return current;
      const usedElsewhere = current.reduce((sum, item, itemIndex) => itemIndex === index || item.unitType !== slot.unitType ? sum : sum + item.quantity, 0);
      const quantity = Math.max(1, Math.min(9, player.units[slot.unitType] - usedElsewhere, slot.quantity + delta));
      return current.map((item, itemIndex) => itemIndex === index ? { ...item, quantity } : item);
    });
  };

  const moveSlot = (index: number, direction: -1 | 1) => {
    setFormation((current) => {
      const targetIndex = index + direction;
      if (targetIndex < 0 || targetIndex >= current.length) return current;
      const next = [...current];
      [next[index], next[targetIndex]] = [next[targetIndex]!, next[index]!];
      return next;
    });
  };

  const addLegion = (unitType: UnitType) => {
    const available = player.units[unitType] - selected[unitType];
    if (available < 1 || formation.length >= 5) return;
    setFormation((current) => [...current, { unitType, quantity: Math.min(9, available) }]);
  };

  return (
    <section className="dispatch-overlay" role="dialog" aria-modal="true" aria-labelledby="dispatch-title">
      <article className="dispatch-panel">
        <header className="dispatch-header">
          <span className="dispatch-kicker">FIELD {target.x}, {target.y} · DEF {target.defense}</span>
          <h2 id="dispatch-title">กองทัพในพื้นที่ปัจจุบัน</h2>
          <button aria-label="ปิดหน้าต่างเลือกกองทัพ" onClick={onCancel}>×</button>
        </header>

        <div className="dispatch-section-title">
          <strong>จัดกระบวนทัพ ({formation.length}/5 กอง)</strong>
          <span className={favorable ? "is-ready" : "is-risky"}>พลัง {selectedPower} / {target.defense}</span>
          <div className="dispatch-tools">
            <button aria-label="จัดกระบวนทัพแนะนำ" onClick={() => setFormation(createDefaultCombatPlan(player.units).formation)}><PixelGlyph name="army" /></button>
            <button aria-label="ล้างกระบวนทัพ" onClick={() => setFormation([])}><PixelGlyph name="gear" /></button>
          </div>
        </div>

        <div className="dispatch-scroll">
          <section className="dispatch-squad enemy-squad">
            <div className="squad-heading">
              <span>แนวป้องกัน · Lv.{target.level}</span>
              <b>{target.neutralUnit ? NEUTRAL_META[target.neutralUnit].label : target.nodeType === "ruins" ? "ผู้พิทักษ์โบราณ" : "กองรักษาการณ์"}</b>
            </div>
            <div className="squad-formation">
              {Array.from({ length: Math.min(7, defenderCount) }, (_, index) => (
                <span className="dispatch-unit defender-unit" key={index}>
                  <i>{target.level}</i>
                  <PixelSoldier action="idle" side="defender" />
                </span>
              ))}
              {defenderCount > 7 && <strong className="formation-more">+{defenderCount - 7}</strong>}
            </div>
            <small>{target.neutralUnit ? enemyTips[target.neutralUnit] : "กองรักษาการณ์ประจำพื้นที่"}</small>
          </section>

          <section className="dispatch-squad player-squad">
            <div className="squad-heading">
              <span>กองทัพของ {player.displayName}</span>
              <b>{favorable ? "ได้เปรียบ" : "เสี่ยงพ่าย"}</b>
            </div>
            <p className="formation-rule">ลำดับ 1 เข้าพื้นที่ก่อน · แต่ละกองบรรจุได้สูงสุด 9 นาย</p>
            <div className="legion-order-list">
              {formation.map((slot, index) => (
                <div className={`legion-order-card unit-${slot.unitType}`} key={`${slot.unitType}:${index}`}>
                  <span className="legion-order-number">{index + 1}</span>
                  <div className="unit-row-copy">
                    <strong>{UNIT_META[slot.unitType].label}</strong>
                    <small>{UNIT_DEFINITIONS[slot.unitType].trait} · เร็ว {UNIT_DEFINITIONS[slot.unitType].speed}</small>
                  </div>
                  <div className="legion-order-controls">
                    <button disabled={index === 0} onClick={() => moveSlot(index, -1)}>↑</button>
                    <button disabled={index === formation.length - 1} onClick={() => moveSlot(index, 1)}>↓</button>
                    <button aria-label={`นำ${UNIT_META[slot.unitType].label}ออก`} onClick={() => setFormation((current) => current.filter((_, itemIndex) => itemIndex !== index))}>×</button>
                  </div>
                  <div className="unit-stepper">
                    <button aria-label={`ลด${UNIT_META[slot.unitType].label}`} disabled={slot.quantity <= 1} onClick={() => changeQuantity(index, -1)}>−</button>
                    <output>{slot.quantity}/9</output>
                    <button aria-label={`เพิ่ม${UNIT_META[slot.unitType].label}`} disabled={slot.quantity >= 9 || selected[slot.unitType] >= player.units[slot.unitType]} onClick={() => changeQuantity(index, 1)}>+</button>
                  </div>
                </div>
              ))}
              {formation.length === 0 && <div className="empty-legions">เลือกประเภททหารด้านล่างเพื่อจัดกอง</div>}
            </div>
            <div className="legion-add-row">
              {unitTypes.map((unitType) => (
                <button key={unitType} disabled={formation.length >= 5 || selected[unitType] >= player.units[unitType]} onClick={() => addLegion(unitType)}>
                  + {UNIT_META[unitType].label} <small>{player.units[unitType] - selected[unitType]}</small>
                </button>
              ))}
            </div>
          </section>

          <section className="dispatch-planning">
            <span className="eyebrow">TACTIC</span>
            <div className="tactic-grid">
              {(Object.keys(TACTIC_META) as TacticType[]).map((type) => (
                <button className={tactic === type ? "is-selected" : ""} key={type} onClick={() => setTactic(type)}><strong>{TACTIC_META[type].label}</strong><small>{TACTIC_META[type].description}</small></button>
              ))}
            </div>
            <span className="eyebrow">COMMANDER</span>
            <div className="hero-select-grid">
              {player.unlockedHeroes.map((id) => (
                <button className={heroId === id ? "is-selected" : ""} key={id} onClick={() => setHeroId(id)}>
                  <span>{HERO_DEFINITIONS[id].name.slice(0, 1)}</span><strong>{HERO_DEFINITIONS[id].name}</strong><small>{HERO_DEFINITIONS[id].skillName}</small>
                </button>
              ))}
            </div>
          </section>
        </div>

        <footer className="dispatch-footer">
          <div className="dispatch-summary">
            <span><PixelGlyph name="army" />{selectedCount} นาย</span>
            <span><PixelGlyph name="season" />ใช้กำลังใจ 1</span>
          </div>
          <div>
            <button className="dispatch-cancel" disabled={busy} onClick={onCancel}>ฝ่ายป้องกัน</button>
            <button className="dispatch-confirm" disabled={busy || selectedCount < 1} onClick={() => onConfirm(plan)}>
              {busy ? "กำลังส่งทัพ…" : "ส่งกองทัพโจมตี"}
            </button>
          </div>
        </footer>
      </article>
    </section>
  );
}

function ArmyScreen({ player, busy, onRecruit }: { player: PlayerStateDto; busy: boolean; onRecruit: (unit: UnitType) => void }) {
  const totalPower = (Object.keys(UNIT_META) as UnitType[]).reduce((total, unitType) => total + player.units[unitType] * UNIT_DEFINITIONS[unitType].power, 0);
  const barracksLevel = getBuildingLevel(player.city?.buildings ?? [], "barracks");
  return (
    <section className="panel-stack">
      <div className="army-banner">
        <div><span className="eyebrow">ARMY POWER</span><strong>{formatNumber(totalPower)}</strong><small>ค่ายทหารระดับ {barracksLevel}</small></div>
        <span className="army-emblem"><PixelGlyph name="army" /></span>
      </div>
      {barracksLevel < 1 && <div className="guide-card"><span><PixelGlyph name="gear" /></span><div><strong>สร้างค่ายทหารก่อน</strong><p>ไปที่เมือง เลือกแปลงว่าง แล้วสร้างค่ายทหาร</p></div></div>}
      <div className="section-heading"><div><span className="eyebrow">HALL OF HEROES</span><h2>นายพลประจำแคว้น</h2></div></div>
      <div className="hero-roster">
        {player.unlockedHeroes.map((heroId) => {
          const hero = HERO_DEFINITIONS[heroId];
          return <article key={heroId}><span>{hero.name.slice(0, 1)}</span><div><strong>{hero.name}</strong><small>{hero.title}</small><p>{hero.skillName} · {hero.description}</p></div></article>;
        })}
      </div>
      <div className="section-heading"><div><span className="eyebrow">ROSTER</span><h2>กองกำลังทั้งหมด</h2></div></div>
      <UnitShop player={player} busy={busy} barracksLevel={barracksLevel} onRecruit={onRecruit} />
    </section>
  );
}

function ResearchScreen({
  player,
  busy,
  nowMs,
  onResearch,
}: {
  player: PlayerStateDto;
  busy: boolean;
  nowMs: number;
  onResearch: (researchId: ResearchId) => void;
}) {
  const academyLevel = getBuildingLevel(player.city?.buildings ?? [], "academy");
  const active = player.activeResearch;
  return (
    <section className="panel-stack research-screen">
      <div className="research-banner">
        <span><PixelGlyph name="book" /></span>
        <div><span className="eyebrow">RESEARCH INSTITUTE</span><h2>สำนักบัณฑิต</h2><p>วิจัยเดินต่อแม้ปิดเกม · ระดับสำนัก {academyLevel}</p></div>
      </div>
      {academyLevel < 1 && <div className="guide-card"><span><PixelGlyph name="gear" /></span><div><strong>ยังไม่มีสำนักบัณฑิต</strong><p>อัปเกรดเมืองหลวงเป็นระดับ 2 แล้วสร้างสำนักบัณฑิต</p></div></div>}
      {active && (
        <div className="research-progress-card">
          <span className="eyebrow">กำลังวิจัย</span>
          <strong>{RESEARCH_DEFINITIONS[active.researchId].name} · Lv.{active.targetLevel}</strong>
          <time>{formatCountdown(Math.max(0, Date.parse(active.completesAt) - nowMs))}</time>
          <div className="construction-progress"><i style={{ width: `${getConstructionProgress({ kind: "upgrade", targetLevel: active.targetLevel, startedAt: active.startedAt, completesAt: active.completesAt }, nowMs)}%` }} /></div>
        </div>
      )}
      <div className="research-tree">
        {(Object.keys(RESEARCH_DEFINITIONS) as ResearchId[]).map((researchId) => {
          const definition = RESEARCH_DEFINITIONS[researchId];
          const level = player.technology[researchId];
          const targetLevel = level + 1;
          const maxed = level >= definition.maxLevel;
          const locked = academyLevel < definition.academyLevel;
          const cost = getResearchCost(researchId, targetLevel);
          const duration = getResearchDurationMs(researchId, targetLevel);
          const canStart = !busy && !active && !locked && !maxed && hasResources(player.resources, cost);
          return (
            <article className={`research-node research-${researchId}`} key={researchId}>
              <span className="research-node-icon"><PixelGlyph name={researchId === "crop_rotation" ? "food" : researchId === "frontier_logistics" ? "map" : "army"} /></span>
              <div><span className="eyebrow">LEVEL {level}/{definition.maxLevel}</span><h3>{definition.name}</h3><p>{definition.description}</p></div>
              <CostLine wallet={cost} />
              <button disabled={!canStart} onClick={() => onResearch(researchId)}>
                {maxed ? "วิจัยเต็มแล้ว" : locked ? `สำนัก Lv.${definition.academyLevel}` : active ? "คิววิจัยไม่ว่าง" : `วิจัย Lv.${targetLevel} · ${formatBuildTime(duration)}`}
              </button>
            </article>
          );
        })}
      </div>
    </section>
  );
}

function QuestPanel({
  player,
  busy,
  onClose,
  onClaim,
}: {
  player: PlayerStateDto;
  busy: boolean;
  onClose: () => void;
  onClaim: (questId: QuestId) => void;
}) {
  return (
    <section className="quest-overlay" role="dialog" aria-modal="true" aria-label="สมุดภารกิจ">
      <article className="quest-panel">
        <header><div><span className="eyebrow">CHAPTER I</span><h2>เส้นทางเจ้าแคว้น</h2></div><button onClick={onClose}>×</button></header>
        <div className="quest-list">
          {(Object.keys(QUEST_DEFINITIONS) as QuestId[]).map((questId, index) => {
            const definition = QUEST_DEFINITIONS[questId];
            const complete = isQuestComplete(player, questId);
            const claimed = player.claimedQuests.includes(questId);
            return (
              <article className={`${complete ? "is-complete" : ""} ${claimed ? "is-claimed" : ""}`} key={questId}>
                <span className="quest-step">{claimed ? "✓" : index + 1}</span>
                <div><strong>{definition.name}</strong><p>{definition.description}</p><small>{formatCost(definition.reward)}</small></div>
                <button disabled={busy || !complete || claimed} onClick={() => onClaim(questId)}>{claimed ? "รับแล้ว" : complete ? "รับรางวัล" : "ยังไม่สำเร็จ"}</button>
              </article>
            );
          })}
        </div>
      </article>
    </section>
  );
}

function UnitShop({ player, busy, barracksLevel, onRecruit }: { player: PlayerStateDto; busy: boolean; barracksLevel: number; onRecruit: (unit: UnitType) => void }) {
  return (
    <div className="unit-list">
      {(Object.keys(UNIT_META) as UnitType[]).map((unit) => {
        const meta = UNIT_META[unit];
        const definition = UNIT_DEFINITIONS[unit];
        const locked = barracksLevel < definition.barracksLevel;
        return (
          <article className="unit-card" key={unit}>
            <span className="unit-icon">
              {unit === "cavalry" ? <PixelGlyph name="horse" /> : <PixelSoldier action="idle" side="attacker" />}
            </span>
            <div className="unit-copy"><h3>{meta.label}</h3><p>มี {player.units[unit]} · อาหาร {definition.foodCost} · พลัง {definition.power}</p></div>
            <button className="small-button" disabled={busy || locked || player.resources.food < definition.foodCost} onClick={() => onRecruit(unit)}>
              {locked ? `ค่าย Lv.${definition.barracksLevel}` : "+1"}
            </button>
          </article>
        );
      })}
    </div>
  );
}

function PixelGlyph({ name }: { name: PixelGlyphName }) {
  const dark = "#5b463b";
  let art;
  switch (name) {
    case "food":
      art = <><path fill={dark} d="M5 3h5v2h2v15h-3v-6H6v-2H3V8h2zm9 0h5v5h2v4h-3v2h-3v6h-3V8h2z"/><path fill="#f4cd52" d="M6 4h3v4H6zm-2 5h5v2H6V9zm11-5h3v4h-3zm0 5h5v2h-5zm-5 0h4v4h-4z"/><path fill="#d89e35" d="M7 5h2v2H7zm8 0h2v2h-2z"/></>;
      break;
    case "wood":
      art = <><path fill={dark} d="M3 6h16v3h2v9h-3v2H4v-2H2V9h1z"/><path fill="#b96d3d" d="M4 8h13v10H4z"/><path fill="#e09a55" d="M15 8h4v10h-4z"/><path fill={dark} d="M16 10h2v2h-2zm0 4h2v2h-2zM6 10h7v2H6zm2 4h5v2H8z"/></>;
      break;
    case "stone":
      art = <><path fill={dark} d="M7 3h9v2h3v4h2v8h-3v3H5v-2H2V9h2V6h3z"/><path fill="#d9d4c2" d="M7 5h8v2h3v9h-3v2H6v-2H4V9h3z"/><path fill="#a7a59a" d="M4 13h5v5H6v-2H4zm9-8h2v2h3v5h-4V9h-3V7h2z"/><path fill="#f4efda" d="M7 6h4v2H7z"/></>;
      break;
    case "coins":
      art = <><path fill={dark} d="M8 2h8v2h4v4h2v8h-2v4h-4v2H8v-2H4v-4H2V8h2V4h4z"/><path fill="#ffd85c" d="M8 4h8v2h3v3h2v6h-2v3h-3v2H8v-2H5v-3H3V9h2V6h3z"/><path fill="#e6a835" d="M9 7h7v2h2v6h-2v2H9v-2H7V9h2z"/><path fill="#fff09a" d="M9 7h4v2H9z"/><path fill="#f9ca45" d="M10 9h5v6h-5z"/></>;
      break;
    case "gift":
      art = <><path fill={dark} d="M3 8h7V6H7V3h4l1 3 1-3h4v3h-3v2h7v5h-2v8H5v-8H3z"/><path fill="#efbd48" d="M5 10h14v2H5zm2 4h10v5H7z"/><path fill="#dc604c" d="M10 8h4v12h-4zM8 4h2v2H8zm6 0h2v2h-2z"/></>;
      break;
    case "season":
      art = <><path fill={dark} d="M6 9h2V6h4v2h4v2h3v2h2v6H3v-6h3z"/><path fill="#fff6df" d="M5 13h3v-2h3V8h1v2h4v2h3v4H5z"/><path fill="#f3c44f" d="M4 4h2V2h2v2h2v2H8v2H6V6H4z"/></>;
      break;
    case "book":
      art = <><path fill={dark} d="M3 3h16v2h2v16H5v-2H3z"/><path fill="#e9b65b" d="M5 5h12v14H5z"/><path fill="#fff1c5" d="M8 6h8v12H8z"/><path fill="#b56d43" d="M5 5h3v14H5zm5 3h4v2h-4zm0 4h5v2h-5z"/></>;
      break;
    case "crystal":
      art = <><path fill={dark} d="M8 3h8v2h3v4h2v6h-3v3h-3v3H9v-2H6v-3H3V9h2V6h3z"/><path fill="#d7eef0" d="M8 5h7v2h3v7h-2v3h-3v2H9v-2H7v-3H5V9h3z"/><path fill="#8ec1c8" d="M8 5h3v12H8V14H5V9h3zm3 12h5v-3h2v-4h-4z"/><path fill="#f6ffff" d="M11 6h4v2h-4z"/></>;
      break;
    case "scout":
      art = <><path fill={dark} d="M8 3h7v2h3v3h2v7h-2v3h-4v-2H9v2H6v3H3v-4h2v-3H3V8h2V5h3z"/><path fill="#d7eee4" d="M8 5h6v2h3v7h-3v2H8v-2H5V8h3z"/><path fill="#75b77a" d="M9 7h4v2h2v4h-2v2H9v-2H7V9h2z"/><path fill="#a56c47" d="M6 16h3v2H7v2H4v-2h2z"/></>;
      break;
    case "grid":
      art = <><path fill="#fff3dd" d="M3 3h8v8H3zm10 0h8v8h-8zM3 13h8v8H3zm10 0h8v8h-8z"/><path fill={dark} d="M3 3h8v2H5v6H3zm10 0h8v2h-6v6h-2zM3 13h8v2H5v6H3zm10 0h8v2h-6v6h-2z"/></>;
      break;
    case "profile":
      art = <><path fill={dark} d="M9 3h6v2h2v6h-2v2h3v2h2v6H4v-6h2v-2h3v-2H7V5h2z"/><path fill="#fff4df" d="M9 5h6v6H9zm-2 10h10v2h2v2H5v-2h2z"/><path fill="#dbbc8a" d="M9 5h6v2H9z"/></>;
      break;
    case "quest":
      art = <><path fill={dark} d="M5 2h14v3h2v16H5v-2H3V5h2z"/><path fill="#fff0ce" d="M6 4h11v3h2v12H6z"/><path fill="#e4ad57" d="M6 4h11v3H6zm0 13h13v2H6z"/><path fill="#b56b44" d="M10 8h5v2h2v3h-3v2h-3v-3h2v-2h-3zm1 8h3v2h-3z"/></>;
      break;
    case "gear":
      art = <><path fill={dark} d="M9 2h6v3h3V3h3v6h-2v2h3v5h-3v2h2v3h-6v-2H9v2H3v-3h2v-2H2v-5h3V9H3V3h3v2h3z"/><path fill="#f2c259" d="M10 6h4v2h3v3h2v4h-3v3H8v-2H6v-6h2V8h2z"/><path fill={dark} d="M10 9h5v2h2v4h-2v2h-5v-2H8v-4h2z"/><path fill="#fff2c1" d="M11 11h3v4h-3z"/></>;
      break;
    case "trophy":
      art = <><path fill={dark} d="M5 3h14v3h3v7h-3v3h-4v3h4v3H5v-3h4v-3H5v-3H2V6h3z"/><path fill="#f2c354" d="M7 5h10v7h-2v3H9v-3H7zM4 7h3v4H5V9H4zm13 0h3v2h-1v2h-2zM9 19h6v1H9z"/><path fill="#ffe993" d="M8 5h3v6H8z"/></>;
      break;
    case "crest":
      art = <><path fill="#fff2d5" d="M7 2h10v2h4v10h-2v4h-3v3H8v-3H5v-4H3V4h4z"/><path fill={dark} d="M7 3h10v2h3v9h-2v4h-3v2H9v-2H6v-4H4V5h3z"/><path fill="#e45050" d="M8 5h8v2h2v7h-2v3h-2v2h-4v-2H8v-3H6V7h2z"/><path fill="#f4c447" d="M11 7h3v3h2v3h-3v4h-2v-4H8v-3h3z"/></>;
      break;
    case "bag":
      art = <><path fill="#fff1d8" d="M8 2h8v3h3v4h2v12H3V9h2V5h3z"/><path fill={dark} d="M9 3h6v3h3v4h2v10H4V10h2V6h3z"/><path fill="#e79848" d="M7 9h10v2h2v7H5v-7h2z"/><path fill="#f2c557" d="M9 5h4v3h3v2H8V8h1z"/><path fill="#c76543" d="M9 12h6v2H9z"/></>;
      break;
    case "chat":
      art = <><path fill={dark} d="M4 3h16v2h2v12h-3v3h-5v3h-3v-3H4v-2H2V5h2z"/><path fill="#fff5e5" d="M5 5h14v2h1v9h-3v2h-5v2h-1v-2H5v-2H4V7h1z"/><path fill="#91a8ca" d="M7 10h3v3H7zm7 0h3v3h-3z"/></>;
      break;
    case "city":
      art = <><path fill={dark} d="M3 8h3V4h3v3h6V4h3v4h3v13H3z"/><path fill="#fff0d5" d="M5 10h14v9H5z"/><path fill="#79aab0" d="M6 7h12l2 4H4z"/><path fill="#4e7e83" d="M8 8h8v2H8zM6 13h12v2H6z"/><path fill="#d9b46e" d="M6 16h12v3H6z"/><path fill="#cf5048" d="M10 17h4v4h-4z"/><path fill="#f6e5c5" d="M7 5h1v3H7zm9 0h1v3h-1z"/></>;
      break;
    case "map":
      art = <><path fill={dark} d="M4 3h6l2 2 2-2h6v18h-6l-2-2-2 2H4z"/><path fill="#f7edcf" d="M6 5h4l1 2v10l-2 2H6zm8 1 1-1h3v14h-4z"/><path fill="#70b670" d="M7 9h3v3H8v3H6v-4h1zm8-1h3v5h-2v3h-2v-5h1z"/><path fill="#e0534f" d="M10 6h4v4h-1v3h-2v-3h-1z"/></>;
      break;
    case "army":
      art = <><path fill={dark} d="M8 3h8v2h3v3h2v8h-4v5H7v-5H3V8h2V5h3z"/><path fill="#a9c5c0" d="M8 5h8v2h2v3H6V7h2zm-3 7h14v2h-3v5h-3v-4h-2v4H8v-5H5z"/><path fill="#6f8f88" d="M8 5h3v5H8zm8 7h3v2h-3z"/></>;
      break;
    case "trade":
      art = <><path fill={dark} d="M4 2h16v4h2v5h-2v10H4V11H2V6h2z"/><path fill="#fff0da" d="M6 11h12v8H6z"/><path fill="#d8584d" d="M4 5h16v4H4z"/><path fill="#f2c15b" d="M7 5h3v4H7zm6 0h3v4h-3z"/><path fill="#79a6a9" d="M8 13h8v5H8z"/></>;
      break;
    case "parcel":
      art = <><path fill={dark} d="M5 5h5V2h8v3h3v16H3V5z"/><path fill="#fff2da" d="M5 7h14v12H5z"/><path fill="#e2ac51" d="M7 9h10v8H7z"/><path fill="#bc7547" d="M10 9h4v8h-4z"/><path fill="#eecc75" d="M12 4h4v3h-4z"/></>;
      break;
    case "mail":
      art = <><path fill={dark} d="M3 5h18v15H3z"/><path fill="#fff1d4" d="M5 7h14v10H5z"/><path fill="#d5a25c" d="m5 7 7 6 7-6v3l-7 6-7-6z"/><path fill="#e4544f" d="M15 2h6v7h-6z"/><path fill="#fff1d4" d="M17 3h2v4h-2z"/></>;
      break;
    case "tree":
      art = <><path fill={dark} d="M9 2h7v2h3v4h2v7h-4v3h-3v4h-5v-4H5v-3H2V8h3V5h4z"/><path fill="#4f9558" d="M9 4h6v2h3v4h2v4h-5v3H6v-3H4V9h3V6h2z"/><path fill="#78b967" d="M8 5h5v3H8z"/><path fill="#9b7048" d="M10 15h4v6h-4z"/><path fill="#6b4c39" d="M10 19h4v2h-4z"/></>;
      break;
    case "rock":
      art = <><path fill={dark} d="M7 7h10v3h3v3h2v6H3v-6h2v-3h2z"/><path fill="#b6bab0" d="M8 8h8v3h3v6H5v-4h2z"/><path fill="#e0dfd1" d="M9 9h5v2h3v2h-6v2H7v-3h2z"/><path fill="#929b91" d="M14 13h5v4h-7v-2h2z"/></>;
      break;
    case "bush":
      art = <><path fill={dark} d="M6 8h4V5h6v3h4v3h2v7H2v-7h4z"/><path fill="#5d9d57" d="M7 9h4V7h4v3h4v6H4v-4h3z"/><path fill="#83bd65" d="M8 9h3v3H7v-2h1zm6 2h4v3h-4z"/><path fill="#6c4c38" d="M10 16h5v3h-5z"/></>;
      break;
    case "stump":
      art = <><path fill={dark} d="M7 8h10v3h2v9H5v-9h2z"/><path fill="#b17a4b" d="M7 10h10v8H7z"/><path fill="#d8a45b" d="M8 9h8v4H8z"/><path fill={dark} d="M10 10h4v2h-4zM7 15h2v3H7zm8 0h2v3h-2z"/></>;
      break;
    case "flowers":
      art = <><path fill="#4c884f" d="M11 8h2v12h-2zm-5 5h5v2H6zm7 2h5v2h-5z"/><path fill="#fff0ce" d="M8 7h2V5h4v2h2v4h-2v2h-4v-2H8z"/><path fill="#e45a52" d="M10 7h4v4h-4z"/><path fill="#f5cf52" d="M11 8h2v2h-2z"/></>;
      break;
    case "ruins":
      art = <><path fill={dark} d="M5 4h14v3h2v14H3V7h2z"/><path fill="#c8b889" d="M6 7h4v12H5V9h1zm8 0h4v3h1v9h-5z"/><path fill="#8d896d" d="M10 9h4v10h-4z"/><path fill="#eee0af" d="M6 7h4v3H6zm8 0h4v3h-4z"/></>;
      break;
    case "horse":
      art = <><path fill={dark} d="M12 3h6v3h2v6h-3v3h-2v5h-3v-5H9v5H6v-6H3v-5h5V6h4z"/><path fill="#fff0d2" d="M13 5h4v2h2v4h-3v3H8v-2H5v-2h5V7h3z"/><path fill="#b77a4b" d="M8 10h8v4H8z"/><path fill="#e3b464" d="M4 8h5v4H5v-2H4z"/><path fill={dark} d="M16 7h2v2h-2z"/></>;
      break;
  }
  return <svg className={`pixel-glyph glyph-${name}`} viewBox="0 0 24 24" shapeRendering="crispEdges" aria-hidden="true">{art}</svg>;
}

function NavButton({ active, icon, label, onClick }: { active: boolean; icon: PixelGlyphName; label: string; onClick: () => void }) {
  return <button className={active ? "nav-button is-active" : "nav-button"} aria-label={label} onClick={onClick}><span><PixelGlyph name={icon} /></span><small>{label}</small></button>;
}

function PixelSoldier({
  action,
  side,
  defeated = false,
  style,
}: {
  action: "idle" | "walk" | "attack" | "fall";
  side: "attacker" | "defender";
  defeated?: boolean;
  style?: CSSProperties;
}) {
  return <span className={`pixel-soldier action-${action} side-${side} ${defeated ? "is-defeated" : ""}`} style={style} aria-hidden="true" />;
}

function BattleScene({ result, replay, onComplete }: { result: CaptureResponse; replay: BattleReplayDto; onComplete: () => void }) {
  const [elapsed, setElapsed] = useState(0);
  const [skipped, setSkipped] = useState(false);

  useEffect(() => {
    if (skipped) {
      setElapsed(replay.durationMs);
      return;
    }
    const startedAt = performance.now();
    const timer = window.setInterval(() => {
      setElapsed(Math.min(replay.durationMs, performance.now() - startedAt));
    }, 60);
    return () => window.clearInterval(timer);
  }, [replay, skipped]);

  const visibleEvents = replay.events.filter((event) => event.tick <= elapsed);
  const latestEvents = visibleEvents.slice(-3);
  const recentSkill = [...visibleEvents].reverse().find((event) => event.type === "skill" && elapsed - event.tick < 950);
  const finished = elapsed >= replay.durationMs;
  const firstAttackTick = replay.events.find((event) => event.type === "attack")?.tick ?? 1_200;
  const approachEndsAt = Math.max(650, firstAttackTick - 150);
  const approachProgress = Math.min(1, elapsed / approachEndsAt);
  const clashed = approachProgress >= 1;
  const actorState = (actor: BattleActorDto) => {
    const damage = visibleEvents
      .filter((event) => event.type === "hit" && event.targetId === actor.id)
      .reduce((total, event) => total + (event.damage ?? 0), 0);
    const defeated = visibleEvents.some((event) => event.type === "defeat" && event.actorId === actor.id);
    const attacking = visibleEvents.some(
      (event) => event.type === "attack" && event.actorId === actor.id && elapsed - event.tick < 360,
    );
    const hit = visibleEvents.some(
      (event) => event.type === "hit" && event.targetId === actor.id && elapsed - event.tick < 260,
    );
    return { hp: defeated ? 0 : Math.max(0, actor.maxHp - damage), defeated, attacking, hit };
  };
  const phaseText = finished
    ? replay.winner === "attacker" ? "แนวรับแตก — เราชนะ" : "กองทัพเราถูกตีแตก"
    : clashed ? "แนวรบปะทะ — กำลังต่อสู้" : "ทั้งสองกองกำลังเดินเข้าประชิด";
  const visualUnits = replay.actors.flatMap((actor) => {
    const displayed = Math.min(6, Math.max(1, actor.quantity));
    return Array.from({ length: displayed }, (_, index) => ({ actor, displayed, index }));
  });

  return (
    <section className={`battle-overlay terrain-${replay.terrain}`} role="dialog" aria-modal="true" aria-label="ฉากต่อสู้">
      <header className="battle-header">
        <div><span className="eyebrow">BATTLE · FIELD {result.tile.x}, {result.tile.y}</span><h2>ศึกชิงผืนดิน</h2></div>
        <button className="skip-battle" onClick={() => setSkipped(true)}>ข้าม ›</button>
      </header>

      <div className="power-versus">
        <span><b>{replay.attackerPower}</b> {HERO_DEFINITIONS[replay.heroId].name} · {TACTIC_META[replay.tactic].label}</span><i>VS</i><span>{replay.actors.find((actor) => actor.side === "defender")?.label ?? "กองรักษาการณ์"} <b>{replay.defenderPower}</b></span>
      </div>

      <div className={`battle-arena ${clashed ? "is-clashed" : "is-approaching"}`}>
        <div className="battle-horizon" />
        <div className="battle-phase"><i />{phaseText}</div>
        <div className="battle-front-line"><span><PixelGlyph name="army" /></span></div>
        {recentSkill && <div className={`battle-skill-flash side-${replay.actors.find((actor) => actor.id === recentSkill.actorId)?.side ?? "attacker"}`}><span>✦</span>{recentSkill.label}</div>}

        {replay.actors.map((actor) => (
          <div
            className={`battle-squad-tag side-${actor.side}`}
            style={{ top: `${42 + actor.lane * 18}%` }}
            key={`tag:${actor.id}`}
          >
            <span>{actor.label}</span><b>×{actor.quantity}</b>
          </div>
        ))}

        {visualUnits.map(({ actor, displayed, index }) => {
          const state = actorState(actor);
          const row = Math.floor(index / 3);
          const column = index % 3;
          const laneY = 53 + actor.lane * 19;
          const y = laneY + row * 10 + (column - 1) * 2.8;
          const startX = actor.side === "attacker" ? 3 - column * 3.2 : 97 + column * 3.2;
          const contactX = actor.side === "attacker"
            ? (actor.role === "ranged" ? 29 : 45) - column * 4.2
            : (actor.role === "ranged" ? 71 : 55) + column * 4.2;
          const arrivalMs = actor.role === "ranged" ? 760 + (5 - actor.speed) * 70 : 1_020 + (8 - actor.speed) * 80;
          const actorProgress = Math.min(1, elapsed / Math.max(500, arrivalMs));
          const actorApproach = 1 - (1 - actorProgress) ** 3;
          const actorClashed = actorProgress >= 1;
          const lunge = state.attacking ? (actor.side === "attacker" ? 3.2 : -3.2) : 0;
          const recoil = state.hit ? (actor.side === "attacker" ? -1.8 : 1.8) : 0;
          const x = startX + (contactX - startX) * actorApproach + lunge + recoil;
          const aliveCount = state.defeated ? 0 : Math.max(1, Math.ceil(displayed * state.hp / actor.maxHp));
          const unitDefeated = index >= aliveCount;
          const action = unitDefeated ? "fall" : !actorClashed ? "walk" : state.attacking ? "attack" : "idle";
          return (
            <div
              className={`battle-unit side-${actor.side} ${state.hit ? "is-hit" : ""} ${unitDefeated ? "is-defeated" : ""}`}
              style={{ left: `${x}%`, top: `${y}%`, zIndex: 8 + Math.round(y) }}
              key={`${actor.id}:${index}`}
            >
              {!unitDefeated && <span className="battle-unit-level">{isUnitType(actor.type) ? UNIT_DEFINITIONS[actor.type].power : result.tile.level}</span>}
              {!unitDefeated && <span className="battle-unit-hp"><i style={{ width: `${Math.max(0, state.hp / actor.maxHp * 100)}%` }} /></span>}
              <PixelSoldier
                action={action}
                side={actor.side}
                defeated={unitDefeated}
                style={{ animationDelay: `-${index * 75}ms` }}
              />
            </div>
          );
        })}

        {visibleEvents.filter((event) => event.type === "hit" && elapsed - event.tick < 420).map((event) => {
          const target = replay.actors.find((actor) => actor.id === event.targetId);
          return (
            <span
              className={`damage-pop target-${target?.side ?? "defender"}`}
              style={{ top: `${49 + (target?.lane ?? 0) * 18}%` }}
              key={`${event.tick}:${event.actorId}`}
            >
              <i>✦</i>-{event.damage}
            </span>
          );
        })}
      </div>

      <div className="battle-log" aria-live="polite">
        {latestEvents.map((event) => <span key={`${event.tick}:${event.type}:${event.actorId}`}>{battleEventText(event, replay)}</span>)}
      </div>

      <div className={`battle-result ${finished ? "is-visible" : ""}`}>
        <span className="result-seal">{replay.winner === "attacker" ? "ชัย" : "พ่าย"}</span>
        <div>
          <span className="eyebrow">{replay.winner === "attacker" ? "VICTORY" : "DEFEAT"}</span>
          <h3>{replay.winner === "attacker" ? "ยึดผืนดินสำเร็จ" : "กองทัพต้องถอยกลับ"}</h3>
          <p>{replay.winner === "attacker" ? `รับ${RESOURCE_META[result.tile.resourceType].label} ${result.tile.level * 75} และผลผลิต +${result.tile.productionPerHour}/ชม.` : "เพิ่มกำลังทหารแล้วลองโจมตีอีกครั้ง"}</p>
        </div>
        <button disabled={!finished} onClick={onComplete}>กลับสู่แผนที่</button>
      </div>
    </section>
  );
}

function battleEventText(event: BattleEventDto, replay: BattleReplayDto): string {
  const actor = replay.actors.find((item) => item.id === event.actorId);
  const target = replay.actors.find((item) => item.id === event.targetId);
  if (event.type === "move") return `${actor?.label ?? "กองทัพ"} เข้าประจำแนวรบ`;
  if (event.type === "attack") return `${actor?.label ?? "กองทัพ"} โจมตี ${target?.label ?? "ศัตรู"}`;
  if (event.type === "skill") return event.label ?? `${actor?.label ?? "กองทัพ"} ใช้สกิล`;
  if (event.type === "hit") return `${target?.label ?? "เป้าหมาย"} เสียพลัง ${event.damage ?? 0}`;
  if (event.type === "heal") return `${target?.label ?? "เป้าหมาย"} ฟื้นพลัง ${event.healing ?? 0}`;
  if (event.type === "defeat") return `${actor?.label ?? "กองทัพ"} ถูกตีแตก`;
  return `${actor?.label ?? "กองทัพ"} เป็นฝ่ายชนะ`;
}
